import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Stethoscope, HeartPulse, Heart, Scale, Thermometer, Wind, Send, Image as ImageIcon, Bookmark, Lightbulb, Pencil, Play, ChevronRight, ChevronLeft as ChevronLeftIcon, FlaskConical, Activity, Sparkles, Trash2, MoreHorizontal, Edit2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import AppShell from '../../components/layout/AppShell';
import { useCase, useCaseInputs, useAddClinicalInput, useEditClinicalInput, useDeleteClinicalInput, useLikeClinicalInput, useLikeCase, useSaveCase, useSavedCases, useDeleteCase } from '../../features/cases/hooks/use-cases';
import { makeCaseChallenge } from '../../features/cases/api/challenges-api';
import { timeAgo } from '../../utils/timeAgo';
import { useAuth } from '../../contexts/AuthContext';
import ShareCaseWizard from '../../features/cases/components/ShareCaseWizard';
import { formatMediaUrl } from '../../features/cases/api/media-api';
import InlineDocumentViewer from '../../features/cases/components/InlineDocumentViewer';
import { isCoFounder } from '../../utils/cofounderBadge';

const getAvatar = (name) => {
  const initials = (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  return initials;
};

// ─── Media Lightbox ───────────────────────────────────────────────────────────
const MediaLightbox = ({ items, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const item = items[idx];
  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        className="absolute top-5 right-5 text-white/70 hover:text-white bg-white/10 rounded-full p-2.5 transition-colors"
        onClick={onClose}
      >
        <X size={22} />
      </button>

      {items.length > 1 && idx > 0 && (
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 rounded-full p-3 transition-colors"
          onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }}
        >
          <ChevronLeftIcon size={22} />
        </button>
      )}

      {items.length > 1 && idx < items.length - 1 && (
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 rounded-full p-3 transition-colors"
          onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }}
        >
          <ChevronRight size={22} />
        </button>
      )}

      <div className="max-w-5xl max-h-[90vh] w-full px-16" onClick={(e) => e.stopPropagation()}>
        {item.type === 'VIDEO' ? (
          <video
            src={formatMediaUrl(item.url)}
            controls
            autoPlay
            preload="metadata"
            playsInline
            className="max-h-[85vh] w-full rounded-xl object-contain"
          />
        ) : (
          <img
            src={formatMediaUrl(item.url)}
            alt={item.filename || 'Clinical media'}
            className="max-h-[85vh] w-full object-contain rounded-xl"
          />
        )}
        {items.length > 1 && (
          <p className="text-white/50 text-xs text-center mt-3">{idx + 1} / {items.length}</p>
        )}
      </div>
    </div>
  );
};

// ─── Section Media Gallery ────────────────────────────────────────────────────
const SectionMediaGallery = ({ items = [] }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!items || items.length === 0) return null;

  const images = items.filter((m) => m.type === 'IMAGE');
  const videos = items.filter((m) => m.type === 'VIDEO');

  return (
    <div className="mt-3 space-y-3">
      {images.length > 0 && (
        <div className={`grid gap-3 ${images.length === 1 ? 'grid-cols-1 max-w-sm' : images.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {images.map((item, i) => (
            <div
              key={i}
              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group border border-gray-200 bg-gray-100"
              onClick={() => setLightboxIdx(items.indexOf(item))}
            >
              <img
                src={formatMediaUrl(item.url)}
                alt={item.filename || `Image ${i + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <ImageIcon size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
              </div>
            </div>
          ))}
        </div>
      )}

      {videos.map((item, i) => (
        <div
          key={i}
          className="relative max-w-sm rounded-lg overflow-hidden cursor-pointer group border border-gray-200 bg-black"
          onClick={() => setLightboxIdx(items.indexOf(item))}
        >
          <video src={formatMediaUrl(item.url)} muted preload="metadata" playsInline className="w-full max-h-48 object-contain pointer-events-none" />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
            <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <Play size={18} className="text-gray-900 ml-0.5" />
            </div>
          </div>
        </div>
      ))}

      {lightboxIdx !== null && (
        <MediaLightbox items={items} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </div>
  );
};

const ClinicalInputItem = ({ input, depth = 0, caseId, user }) => {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editText, setEditText] = useState(input.content);
  const [replyError, setReplyError] = useState('');
  const [editError, setEditError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteInputError, setDeleteInputError] = useState('');

  const menuRef = useRef(null);
  const editTextareaRef = useRef(null);

  const { mutate: likeInput, isPending: isLikingInput } = useLikeClinicalInput();
  const { mutate: addInput, isPending: isAdding } = useAddClinicalInput();
  const { mutate: editInput, isPending: isEditingInput } = useEditClinicalInput();
  const { mutate: deleteInput, isPending: isDeletingInput } = useDeleteClinicalInput();

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Auto-resize textarea to fit full answer when entering edit mode or typing
  useEffect(() => {
    if (isEditing && editTextareaRef.current) {
      editTextareaRef.current.style.height = 'auto';
      editTextareaRef.current.style.height = `${Math.max(120, editTextareaRef.current.scrollHeight)}px`;
    }
  }, [isEditing, editText]);

  const isLiked = input.likedBy?.includes(user?.id);
  const likesCount = input.likes || 0;
  
  const isMyInput = Boolean(
    (user?.id && (user.id === input.authorId || user.id === input.author?.id)) ||
    (user?.name && (input.author?.name === user.name || !input.author?.name))
  );
  const authorObj = input.author || {};
  const isAuthorCoFounder = isCoFounder(authorObj) || (isMyInput && isCoFounder(user)) || isCoFounder({ jobRole: authorObj.jobRole || (isMyInput ? user?.jobRole : null) });
  const rawInputAuthor = authorObj.name || (isMyInput ? user?.name : 'Unknown User');
  const inputAuthor = authorObj.role === 'student' || isAuthorCoFounder ? rawInputAuthor : (rawInputAuthor.startsWith('Dr.') ? rawInputAuthor : `Dr. ${rawInputAuthor}`);
  const inputAvatar = authorObj.avatarUrl || (isMyInput ? user?.avatarUrl : null);
  const inputRole = authorObj.jobRole || (isMyInput ? user?.jobRole : null) || (authorObj.role === 'student' ? 'Student' : (authorObj.specialization || 'Veterinarian'));

  const isReply = depth > 0 || Boolean(input.parentId);
  const maxEditLength = isReply ? 400 : 1000;
  const isEdited = input.updatedAt && input.createdAt && (new Date(input.updatedAt).getTime() - new Date(input.createdAt).getTime() > 1000);

  const handleReplySubmit = (e) => {
    e.preventDefault();
    const wordsCount = replyText.trim().split(/\s+/).filter(w => w.length > 0).length;
    if (wordsCount < 5) {
      setReplyError('Reply must be at least 5 words.');
      return;
    }
    if (replyText.length > 300) {
      setReplyError('Reply cannot exceed 300 characters.');
      return;
    }
    
    setReplyError('');
    addInput({ caseId, content: replyText, parentId: input.id }, {
      onSuccess: () => {
        setReplyText('');
        setIsReplying(false);
        setShowReplies(true);
      },
      onError: (err) => setReplyError(err.response?.data?.message || err.message)
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const trimmed = editText.trim();
    if (!trimmed) return;
    if (trimmed.length < 10) {
      setEditError('Clinical input must be at least 10 characters long.');
      return;
    }
    if (trimmed.length > maxEditLength) {
      setEditError(`Input cannot exceed ${maxEditLength} characters.`);
      return;
    }
    setEditError('');
    editInput({ inputId: input.id, content: trimmed }, {
      onSuccess: () => {
        setIsEditing(false);
      },
      onError: (err) => setEditError(err.response?.data?.message || err.message)
    });
  };

  const handleDeleteConfirm = () => {
    setDeleteInputError('');
    deleteInput(input.id, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
      },
      onError: (err) => {
        setDeleteInputError(err?.response?.data?.message || 'Failed to delete clinical input.');
      }
    });
  };

  return (
    <div className={`flex gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100 ${depth > 0 ? 'ml-6 mt-3 border-l-2 border-l-[#6b46c1]/20 rounded-l-none' : ''}`}>
      <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden border border-indigo-200">
        {inputAvatar ? (
          <img src={inputAvatar} alt={inputAuthor} className="w-full h-full object-cover" />
        ) : (
          getAvatar(inputAuthor)
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-gray-900 text-[13px] truncate">
                {inputAuthor}
              </span>
              {isAuthorCoFounder ? (
                <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-indigo-100 shrink-0">
                  Co-Founder · Furcare
                </span>
              ) : null}
            </div>
            {!isAuthorCoFounder && inputRole && (
              <span className="text-[11px] font-medium text-indigo-600">{inputRole}</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-[11px] whitespace-nowrap flex items-center gap-1">
              {timeAgo(input.createdAt)}
              {isEdited && <span className="italic opacity-80">(edited)</span>}
            </span>

            {isMyInput && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  aria-label="More options"
                >
                  <MoreHorizontal size={14} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 animate-in fade-in zoom-in-95 duration-100">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(true);
                        setEditText(input.content);
                        setMenuOpen(false);
                        setIsReplying(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Edit2 size={13} className="text-gray-500" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteInputError('');
                        setConfirmDeleteOpen(true);
                        setMenuOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 size={13} className="text-red-500" /> Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {isEditing ? (
          <form onSubmit={handleEditSubmit} className="mt-2">
            <textarea
              ref={editTextareaRef}
              value={editText}
              maxLength={maxEditLength}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6b46c1]/20 focus:border-[#6b46c1] transition-all resize-y min-h-[120px] max-h-[500px] overflow-y-auto leading-relaxed break-words [overflow-wrap:anywhere]"
              disabled={isEditingInput}
              autoFocus
            />
            <div className="flex items-center justify-between mt-1">
              <span className={`text-[11px] font-medium transition-colors ${
                editText.length >= maxEditLength
                  ? 'text-red-500 font-bold'
                  : editText.length >= maxEditLength * 0.9
                  ? 'text-amber-500 font-semibold'
                  : 'text-gray-400'
              }`}>
                {editText.length}/{maxEditLength}
              </span>
              <div className="flex gap-2">
                <button 
                  type="button" 
                  onClick={() => { setIsEditing(false); setEditText(input.content); setEditError(''); }} 
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  disabled={isEditingInput}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-3 py-1.5 text-xs font-medium text-white bg-[#6b46c1] hover:bg-[#553098] rounded-md transition-colors disabled:opacity-50"
                  disabled={!editText.trim() || isEditingInput || editText.trim().length < 10 || editText.length > maxEditLength}
                >
                  {isEditingInput ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
            {editError && <div className="text-red-500 text-xs mt-1.5">{editError}</div>}
          </form>
        ) : (
          <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed mt-2 break-words break-all [overflow-wrap:anywhere]">
            {input.content}
          </p>
        )}

        {/* Action Bar */}
        {!isEditing && (
          <div className="flex items-center gap-4 mt-3">
            <button 
              onClick={() => likeInput(input.id)}
              disabled={isLikingInput}
              className={`flex items-center gap-1 text-xs transition-colors ${isLiked ? 'text-red-500 font-medium' : 'text-gray-500 hover:text-red-500 font-medium'}`}
            >
              <Heart size={14} className={isLiked ? 'fill-current' : ''} /> {likesCount > 0 && `${likesCount} `}Appreciate
            </button>
            <button onClick={() => setIsReplying(!isReplying)} className="text-xs text-gray-500 hover:text-[#6b46c1] font-medium transition-colors">Reply</button>
            {input.replies && input.replies.length > 0 && (
              <button 
                onClick={() => setShowReplies(!showReplies)} 
                className="text-xs text-[#6b46c1] hover:text-[#553098] font-medium transition-colors"
              >
                {showReplies ? 'Hide Replies' : `View Replies (${input.replies.length})`}
              </button>
            )}
          </div>
        )}

        {isReplying && (
          <form onSubmit={handleReplySubmit} className="mt-3">
            <div className="relative">
              <textarea
                value={replyText}
                maxLength={300}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="w-full px-3 py-2 pb-6 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#6b46c1] transition-all resize-y min-h-[60px] break-words [overflow-wrap:anywhere]"
              />
              <div className="absolute bottom-2 left-3 text-[10px] text-gray-400">
                {replyText.length} / 300 characters
              </div>
            </div>
            {replyError && <div className="text-red-500 text-xs mt-1">{replyError}</div>}
            <div className="flex gap-2 mt-2 justify-end">
              <button type="button" onClick={() => { setIsReplying(false); setReplyError(''); }} className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-1">Cancel</button>
              <button type="submit" disabled={isAdding} className="text-xs text-white bg-[#6b46c1] hover:bg-[#553098] px-3 py-1 rounded-md font-medium transition-colors disabled:opacity-50">{isAdding ? 'Replying...' : 'Reply'}</button>
            </div>
          </form>
        )}
        
        {/* Render Replies */}
        {showReplies && input.replies && input.replies.length > 0 && (
          <div className="mt-3 space-y-3">
            {input.replies.map(reply => <ClinicalInputItem key={reply.id} input={reply} depth={depth + 1} caseId={caseId} user={user} />)}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {confirmDeleteOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {isReply ? 'Delete Reply' : 'Delete Clinical Input'}
                </h3>
                <p className="text-sm text-gray-500">
                  {isReply 
                    ? 'Are you sure you want to delete this reply? This action cannot be undone.'
                    : 'Are you sure you want to delete this clinical input? This action cannot be undone and will delete all replies.'}
                </p>
                {deleteInputError && (
                  <p className="text-xs text-red-600 mt-2 font-medium bg-red-50 p-2 rounded border border-red-100">
                    {deleteInputError}
                  </p>
                )}
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setConfirmDeleteOpen(false); setDeleteInputError(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-lg transition-colors"
                  disabled={isDeletingInput}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  disabled={isDeletingInput}
                >
                  {isDeletingInput ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DISCUSSION_TYPES_MAP = {
  research_paper:       { label: 'Research Paper',       emoji: '🔬', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  news:                 { label: 'News',                 emoji: '📰', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  clinical_observation: { label: 'Clinical Observation', emoji: '💬', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  my_research:          { label: 'My Research',          emoji: '📝', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const CaseDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading } = useCase(id);
  const { data: inputsData, isLoading: isLoadingInputs } = useCaseInputs(id);
  const { mutate: addInput, isPending: isAdding } = useAddClinicalInput();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { mutate: likeCase, isPending: isLiking } = useLikeCase();
  const { mutate: saveCase, isPending: isSaving } = useSaveCase();
  const { mutate: removeCase, isPending: isDeleting } = useDeleteCase();
  const { data: savedCasesData } = useSavedCases(1, 100);
  
  const [newInput, setNewInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [editWizardOpen, setEditWizardOpen] = useState(false);
  const [quickMediaLightboxIndex, setQuickMediaLightboxIndex] = useState(null);
  const [isTogglingChallenge, setIsTogglingChallenge] = useState(false);
  const [challengeMsg, setChallengeMsg] = useState('');

  const [confirmDeleteCaseOpen, setConfirmDeleteCaseOpen] = useState(false);
  const [deleteCaseError, setDeleteCaseError] = useState('');

  const caseData = data?.data;
  const isPublishedChallenge = Boolean(caseData?.challenges?.some(ch => ch.isPublished));
  const isQuickDiscussion = caseData?.postType === 'quick_discussion';

  const handleDeleteCaseConfirm = () => {
    setDeleteCaseError('');
    removeCase(id, {
      onSuccess: () => {
        setConfirmDeleteCaseOpen(false);
        navigate('/cases');
      },
      onError: (err) => {
        setDeleteCaseError(err?.response?.data?.message || 'Failed to delete case.');
      }
    });
  };

  const handleToggleChallenge = async () => {
    try {
      setIsTogglingChallenge(true);
      setChallengeMsg('');
      await makeCaseChallenge(id);
      await queryClient.invalidateQueries(['case', id]);
      await queryClient.invalidateQueries(['challenges']);
      setChallengeMsg(isPublishedChallenge ? 'Removed from student challenges.' : 'Published as a student challenge!');
      setTimeout(() => setChallengeMsg(''), 4000);
    } catch (err) {
      console.error('Failed to toggle challenge:', err);
      setChallengeMsg(err?.response?.data?.message || 'Failed to update challenge status.');
      setTimeout(() => setChallengeMsg(''), 4000);
    } finally {
      setIsTogglingChallenge(false);
    }
  };

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      </AppShell>
    );
  }

  const title = caseData?.title || 'Unknown Case';
  const isAuthorCurrentUser = Boolean(user?.id && (user.id === caseData?.authorId || user.id === caseData?.author?.id));
  const mainAuthorObj = caseData?.author || {};
  const rawAuthorName = mainAuthorObj.name || (isAuthorCurrentUser ? user?.name : 'Unknown Author');
  const authorName = mainAuthorObj.role === 'student' || isCoFounder(mainAuthorObj) || (isAuthorCurrentUser && isCoFounder(user)) ? rawAuthorName : (rawAuthorName.startsWith('Dr.') ? rawAuthorName : `Dr. ${rawAuthorName}`);
  const authorJobRole = mainAuthorObj.jobRole || (isAuthorCurrentUser ? user?.jobRole : null) || (mainAuthorObj.role === 'student' ? 'Student' : (mainAuthorObj.specialization || 'Veterinarian'));
  const authorAvatar = mainAuthorObj.avatarUrl || (isAuthorCurrentUser ? user?.avatarUrl : null);
  const speciesText = caseData?.species === 'Other' ? (caseData?.customSpecies || 'Other') : caseData?.species;
  const breedText = caseData?.breed;

  const formattedPatient = [
    caseData?.species,
    caseData?.breed,
    caseData?.ageValue ? `${caseData.ageValue} ${caseData.ageUnit || 'years'}` : null,
    caseData?.sex,
    caseData?.sterilization
  ].filter(Boolean).join(', ') || caseData?.patientInfo;
  
  const chiefComplaint = caseData?.chiefComplaint || 'No chief complaint recorded.';
  const physicalExam = caseData?.physicalExam || 'No physical exam findings recorded.';
  const differentialDiagnosis = caseData?.differentialDiagnosis || caseData?.findings || '';
  const diagnosticsPlan = caseData?.diagnosticsPlan || caseData?.assessment || '';
  const recommendedTreatment = caseData?.plan || 'No treatment plan recorded.';
  const diagnosticResults = caseData?.diagnosticResults || '';
  const finalDiagnosis = caseData?.finalDiagnosis || '';
  const prognosis = caseData?.prognosis || '';
  const outcome = caseData?.outcome || '';
  
  const vitalsTemp = caseData?.vitalsTemp || 'N/A';
  const vitalsHeartRate = caseData?.vitalsHeartRate || 'N/A';
  const vitalsRespRate = caseData?.vitalsRespRate || 'N/A';
  const vitalsWeight = caseData?.vitalsWeight || 'N/A';
  
  const mediaBySection = caseData?.mediaBySection || {};
  const clinicalInputs = inputsData?.data || [];
  const keyLearnings = caseData?.keyLearnings || [];

  const isLiked = caseData?.likedBy?.includes(user?.id);
  const likesCount = caseData?.likes || 0;
  const isSaved = savedCasesData?.data?.some(c => c.id === id);

  const handleAddInput = (e) => {
    e.preventDefault();
    if (!newInput.trim()) return;
    
    const wordsCount = newInput.trim().split(/\s+/).filter(w => w.length > 0).length;
    if (wordsCount < 10) {
      setInputError('Input must be at least 10 words.');
      return;
    }
    if (newInput.length > 1000) {
      setInputError('Input cannot exceed 1000 characters.');
      return;
    }
    
    setInputError('');
    addInput({ caseId: id, content: newInput }, {
      onSuccess: () => setNewInput(''),
      onError: (err) => setInputError(err.response?.data?.message || err.message)
    });
  };

  return (
    <AppShell>
      <div className="bg-gray-50/50 min-h-screen pb-20">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-900 flex items-center text-sm font-medium transition-colors"
            >
              <span className="mr-1">{"<"}</span> Back to Cases
            </button>
            <div className="text-xs text-gray-400 font-medium">
              Cases / <span className="text-gray-700">Case Details</span>
            </div>
          </div>

            <div className="flex flex-col lg:flex-row gap-6">
              
              {/* Left Column: Case Details */}
              <div className="flex-1 min-w-0">
                
                {isQuickDiscussion ? (
                  /* Quick Discussion Presentation */
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6 min-w-0 overflow-hidden">
                    {/* Header / Author */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold flex items-center justify-center text-lg overflow-hidden border border-indigo-200 shrink-0 shadow-xs">
                          {authorAvatar ? (
                            <img src={authorAvatar} alt={authorName} className="w-full h-full object-cover" />
                          ) : (
                            getAvatar(authorName)
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-base">{authorName}</h3>
                            {authorJobRole && (
                              <span className="bg-indigo-50 text-indigo-700 px-3 py-0.5 rounded-full text-xs font-semibold border border-indigo-100">
                                {authorJobRole}
                              </span>
                            )}
                            {caseData?.discussionType && DISCUSSION_TYPES_MAP[caseData.discussionType] && (
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${DISCUSSION_TYPES_MAP[caseData.discussionType].badge}`}>
                                {DISCUSSION_TYPES_MAP[caseData.discussionType].emoji} {DISCUSSION_TYPES_MAP[caseData.discussionType].label}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                            <span>{timeAgo(caseData?.createdAt)}</span>
                            <span>•</span>
                            {caseData?.species && (
                              <span className="bg-pink-50 text-pink-700 px-2.5 py-0.5 rounded-full font-medium text-xs border border-pink-100">
                                {caseData.species}
                              </span>
                            )}
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                              caseData?.isRestricted || caseData?.visibility === 'Private'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            }`}>
                              {caseData?.isRestricted || caseData?.visibility === 'Private' ? 'Private' : 'Public'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Post Content / Body */}
                    <div className="space-y-4">
                      {caseData?.title && (
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug break-words [overflow-wrap:anywhere]">
                          {caseData.title}
                        </h1>
                      )}
                      {caseData?.content && (
                        <p className="text-gray-800 text-sm sm:text-base whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere]">
                          {caseData.content}
                        </p>
                      )}

                      {/* Tags */}
                      {caseData?.tags && caseData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-2">
                          {caseData.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs font-medium px-2.5 py-1 bg-indigo-50/70 text-indigo-600 rounded-lg border border-indigo-100 hover:bg-indigo-100 transition-colors">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Attachments Section */}
                    {caseData?.media && caseData.media.length > 0 && (
                      <div className="space-y-4 pt-2 border-t border-gray-100">
                        {/* Document Attachments — LinkedIn-style In-Card Carousel */}
                        {caseData.media.filter(m => m.type === 'DOCUMENT').map((doc, idx) => (
                          <InlineDocumentViewer
                            key={doc.id || idx}
                            url={formatMediaUrl(doc.url)}
                            mediaId={doc.id}
                            filename={doc.filename}
                            mimeType={doc.mimeType}
                            sizeBytes={doc.sizeBytes}
                          />
                        ))}

                        {/* Video Attachments */}
                        {caseData.media.filter(m => m.type === 'VIDEO').map((v, idx) => (
                          <div key={v.id || idx} className="rounded-xl overflow-hidden bg-black max-h-[420px] flex items-center justify-center">
                            <video
                              src={formatMediaUrl(v.url)}
                              controls
                              preload="metadata"
                              playsInline
                              className="w-full max-h-[400px] object-contain rounded-xl"
                            />
                          </div>
                        ))}

                        {/* Image Gallery */}
                        {caseData.media.filter(m => m.type === 'IMAGE').length > 0 && (
                          <div>
                            <div className={`grid gap-3 rounded-xl overflow-hidden ${
                              caseData.media.filter(m => m.type === 'IMAGE').length === 1 ? 'grid-cols-1' : 'grid-cols-2'
                            }`}>
                              {caseData.media.filter(m => m.type === 'IMAGE').map((img, idx) => (
                                <div
                                  key={img.id || idx}
                                  onClick={() => setQuickMediaLightboxIndex(idx)}
                                  className="relative group cursor-pointer overflow-hidden rounded-xl bg-gray-100 border border-gray-200 aspect-[4/3]"
                                >
                                  <img
                                    src={formatMediaUrl(img.url)}
                                    alt={img.filename || 'Post attachment'}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                    <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 text-xs font-semibold px-2.5 py-1 rounded-md shadow-xs transition-opacity">
                                      View
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {quickMediaLightboxIndex !== null && (
                              <MediaLightbox
                                items={caseData.media.filter(m => m.type === 'IMAGE')}
                                startIndex={quickMediaLightboxIndex}
                                onClose={() => setQuickMediaLightboxIndex(null)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Bottom Action Bar */}
                    <div className="pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => likeCase(id)}
                          disabled={isLiking}
                          className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${isLiked ? 'text-red-500 font-medium' : 'text-red-500 hover:text-red-600'}`}
                        >
                          <Heart size={15} className={isLiked ? 'fill-current' : 'fill-red-500 text-red-500'} />
                          {likesCount > 0 ? `${likesCount} Appreciate` : 'Appreciate'}
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => saveCase(id)}
                          disabled={isSaving}
                          className={`flex items-center gap-1.5 text-xs font-semibold border px-3.5 py-1.5 rounded-lg transition-colors ${
                            isSaved ? 'text-indigo-600 border-indigo-600 bg-indigo-50 font-medium' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <Bookmark size={14} className={isSaved ? 'fill-current' : ''} />
                          {isSaved ? 'Saved' : 'Save Post'}
                        </button>

                        {isAuthorCurrentUser && (
                          <>
                            <button
                              onClick={() => setEditWizardOpen(true)}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                            >
                              <Pencil size={13} /> Edit Post
                            </button>
                            <button
                              onClick={() => { setDeleteCaseError(''); setConfirmDeleteCaseOpen(true); }}
                              disabled={isDeleting}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Full Case Presentation */
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-8">
                    
                    {/* Header / Author */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-lg overflow-hidden border border-indigo-200 shrink-0">
                          {authorAvatar ? (
                            <img src={authorAvatar} alt={authorName} className="w-full h-full object-cover" />
                          ) : (
                            getAvatar(authorName)
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-base">{authorName}</h3>
                            <span className="bg-indigo-50 text-indigo-700 px-3 py-0.5 rounded-full text-xs font-semibold border border-indigo-100">
                              {authorJobRole}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                            <span>{timeAgo(caseData?.createdAt)}</span>
                            <span>•</span>
                            {(breedText || speciesText) && (
                              <span className="bg-pink-50 text-pink-700 px-2.5 py-0.5 rounded-full font-medium text-xs border border-pink-100">
                                {breedText || speciesText}
                              </span>
                            )}
                            <span className="bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full font-medium text-xs border border-emerald-100">
                              {caseData?.category || 'Clinical Essentials'}
                            </span>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                              caseData?.isRestricted || caseData?.visibility === 'Private'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            }`}>
                              {caseData?.isRestricted || caseData?.visibility === 'Private' ? 'Private' : 'Public'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Case Title & Badges */}
                      <div>
                        {challengeMsg && (
                          <div className="mb-3 px-3.5 py-2 bg-purple-50 border border-purple-200 text-purple-800 rounded-lg text-xs font-medium animate-in fade-in duration-200">
                            {challengeMsg}
                          </div>
                        )}
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-snug">{title}</h1>
                      </div>
                    </div>

                    {/* Visit Details (Step 1 & Step 2) */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Stethoscope className="text-blue-500" size={18} />
                        <h3 className="text-sm font-semibold text-gray-900">Visit Details</h3>
                      </div>
                      {formattedPatient && (
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold">Patient: </span> 
                          {formattedPatient}
                        </div>
                      )}
                      <div className="text-sm text-gray-700">
                        <span className="font-semibold">Chief Complaint: </span> 
                        {chiefComplaint}
                      </div>
                      {caseData?.reasonForVisit && (
                        <div className="text-sm text-gray-700 mt-1">
                          <span className="font-semibold">History: </span> 
                          {caseData.reasonForVisit}
                        </div>
                      )}
                      {mediaBySection.CHIEF_COMPLAINT?.length > 0 && (
                        <SectionMediaGallery items={mediaBySection.CHIEF_COMPLAINT} />
                      )}
                    </div>

                    {/* Clinical Findings & Vitals (Step 3) */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="text-emerald-500" size={18} />
                        <h3 className="text-sm font-semibold text-gray-900">Clinical Findings &amp; Vitals</h3>
                      </div>

                      {/* Vitals Grid */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vitals</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                            <HeartPulse size={16} className="text-red-500 mb-1" />
                            <span className="text-xs text-gray-500 font-medium">Heart Rate</span>
                            <span className="text-sm font-semibold text-gray-900">{vitalsHeartRate && vitalsHeartRate !== 'N/A' ? (vitalsHeartRate.includes('bpm') ? vitalsHeartRate : `${vitalsHeartRate} bpm`) : 'N/A'}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                            <Wind size={16} className="text-blue-400 mb-1" />
                            <span className="text-xs text-gray-500 font-medium">Resp Rate</span>
                            <span className="text-sm font-semibold text-gray-900">{vitalsRespRate && vitalsRespRate !== 'N/A' ? (vitalsRespRate.includes('brpm') || vitalsRespRate.includes('breaths') ? vitalsRespRate : `${vitalsRespRate} brpm`) : 'N/A'}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                            <Thermometer size={16} className="text-red-400 mb-1" />
                            <span className="text-xs text-gray-500 font-medium">Temperature</span>
                            <span className="text-sm font-semibold text-gray-900">{vitalsTemp && vitalsTemp !== 'N/A' ? (vitalsTemp.includes('°') ? vitalsTemp : `${vitalsTemp}°F`) : 'N/A'}</span>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex flex-col items-center justify-center text-center">
                            <Scale size={16} className="text-purple-400 mb-1" />
                            <span className="text-xs text-gray-500 font-medium">Weight</span>
                            <span className="text-sm font-semibold text-gray-900">{vitalsWeight && vitalsWeight !== 'N/A' ? (vitalsWeight.includes('kg') || vitalsWeight.includes('lbs') ? vitalsWeight : `${vitalsWeight} kg`) : 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="text-sm text-gray-700">
                        <span className="font-semibold">Physical Exam Findings: </span> 
                        {physicalExam}
                      </div>

                      {mediaBySection.CLINICAL_FINDINGS?.length > 0 && (
                        <div className="mt-3">
                          <SectionMediaGallery items={mediaBySection.CLINICAL_FINDINGS} />
                        </div>
                      )}
                    </div>

                    {/* Assessment & Diagnostics (Step 4) */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <FlaskConical className="text-purple-500" size={18} />
                        <h3 className="text-sm font-semibold text-gray-900">Assessment &amp; Diagnostics</h3>
                      </div>
                      {differentialDiagnosis && (
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold">Differential Diagnosis: </span> 
                          {differentialDiagnosis}
                        </div>
                      )}
                      {diagnosticsPlan && (
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold">Diagnostics Plan: </span> 
                          {diagnosticsPlan}
                        </div>
                      )}
                      {diagnosticResults && (
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold">Diagnostic Results: </span> 
                          {diagnosticResults}
                        </div>
                      )}
                      {finalDiagnosis && (
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-semibold">Final Diagnosis: </span> 
                          {finalDiagnosis}
                        </div>
                      )}
                      {prognosis && (
                        <div className="text-sm text-gray-700">
                          <span className="font-semibold">Prognosis: </span> 
                          {prognosis}
                        </div>
                      )}
                      {mediaBySection.DIAGNOSTICS?.length > 0 && (
                        <div className="mt-3">
                          <SectionMediaGallery items={mediaBySection.DIAGNOSTICS} />
                        </div>
                      )}
                    </div>

                    {/* Recommended Treatment Plan & Outcome (Step 5) */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Stethoscope className="text-emerald-500" size={18} />
                        <h3 className="text-sm font-semibold text-gray-900">Recommended Treatment Plan</h3>
                      </div>
                      <div className="text-sm text-gray-700 mb-1">
                        {recommendedTreatment}
                      </div>
                      {outcome && (
                        <div className="text-sm text-gray-700 mt-2">
                          <span className="font-semibold">Case Outcome: </span> 
                          {outcome}
                        </div>
                      )}
                      {mediaBySection.OUTCOME?.length > 0 && (
                        <div className="mt-3">
                          <SectionMediaGallery items={mediaBySection.OUTCOME} />
                        </div>
                      )}
                    </div>

                    {/* Key Learnings (Step 5) */}
                    {keyLearnings.length > 0 && (
                      <div className="bg-purple-50/60 border border-purple-100 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Lightbulb className="text-purple-600" size={18} />
                          <h3 className="text-sm font-semibold text-purple-900">Key Learnings</h3>
                        </div>
                        <ol className="space-y-2">
                          {keyLearnings.map((point, i) => (
                            <li key={i} className="flex gap-2 text-sm text-purple-900">
                              <span className="font-bold text-purple-500 shrink-0">{i + 1}.</span>
                              <span className="leading-relaxed">{point}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Bottom Action Bar (Appreciate / Share / Save / Challenge / Edit) */}
                    <div className="pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <button 
                          onClick={() => likeCase(id)}
                          disabled={isLiking}
                          className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${isLiked ? 'text-red-500 font-medium' : 'text-red-500 hover:text-red-600'}`}
                        >
                          <Heart size={15} className={isLiked ? 'fill-current' : 'fill-red-500 text-red-500'} />
                          {likesCount > 0 ? `${likesCount} Appreciate` : 'Appreciate'}
                        </button>

                      </div>

                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => saveCase(id)}
                          disabled={isSaving}
                          className={`flex items-center gap-1.5 text-xs font-semibold border px-3.5 py-1.5 rounded-lg transition-colors ${
                            isSaved ? 'text-indigo-600 border-indigo-600 bg-indigo-50 font-medium' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <Bookmark size={14} className={isSaved ? 'fill-current' : ''} />
                          {isSaved ? 'Saved' : 'Save Case'}
                        </button>

                        {isAuthorCurrentUser && (
                          <>
                            <button
                              onClick={() => setEditWizardOpen(true)}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                            >
                              <Pencil size={13} /> Edit Case
                            </button>
                            <button
                              onClick={() => { setDeleteCaseError(''); setConfirmDeleteCaseOpen(true); }}
                              disabled={isDeleting}
                              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          </>
                        )}

                        {user?.role === 'admin' && (
                          <button
                            onClick={handleToggleChallenge}
                            disabled={isTogglingChallenge}
                            className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-1.5 rounded-lg border transition-all shadow-xs ${
                              isPublishedChallenge
                                ? 'border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100'
                                : 'border-purple-300 text-purple-800 bg-purple-50 hover:bg-purple-100'
                            }`}
                            title={isPublishedChallenge ? 'Remove this case from student challenges feed' : 'Publish this case as a challenge for students'}
                          >
                            <Sparkles size={13} className={isPublishedChallenge ? 'text-amber-600' : 'text-purple-600'} />
                            {isTogglingChallenge ? 'Updating...' : isPublishedChallenge ? 'Unpublish Challenge' : '🎯 Publish as Challenge'}
                          </button>
                        )}


                      </div>
                    </div>

                  </div>
                )}
              </div>

              {/* Right Column: Clinical Inputs */}
              <div className="lg:w-[400px] flex-shrink-0 flex flex-col space-y-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col sticky top-6 max-h-[calc(100vh-100px)]">
                  <div className="p-5 border-b border-gray-100">
                    <h3 className="text-base font-semibold text-gray-900">
                      Clinical Inputs ({clinicalInputs.length})
                    </h3>
                  </div>

                  {/* Input Form */}
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <form onSubmit={handleAddInput}>
                      <div className="relative">
                        <textarea
                          value={newInput}
                          maxLength={1000}
                          onChange={(e) => setNewInput(e.target.value)}
                          placeholder="Share your clinical insights, recommendations, or ask follow-up questions..."
                          className="w-full px-4 py-3 pb-8 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#6b46c1]/20 focus:border-[#6b46c1] transition-all resize-y min-h-[100px]"
                          disabled={isAdding}
                        />
                        <div className="absolute bottom-3 left-4 text-[11px] text-gray-400">
                          {newInput.length} / 1000 characters
                        </div>
                      </div>
                      {inputError && <div className="text-red-500 text-xs mt-2 px-1">{inputError}</div>}
                      <div className="mt-3 flex justify-end">
                        <button
                          type="submit"
                          disabled={!newInput.trim() || isAdding}
                          className="px-5 py-2.5 bg-[#6b46c1] text-white hover:bg-[#553098] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center rounded-xl font-medium text-sm gap-2"
                        >
                          <Send size={16} />
                          Add Clinical Input
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Inputs List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
                    {isLoadingInputs ? (
                      <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-indigo"></div>
                      </div>
                    ) : clinicalInputs.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">No clinical inputs yet. Be the first to share your thoughts!</p>
                      </div>
                    ) : (
                      clinicalInputs.map((input) => (
                        <ClinicalInputItem key={input.id} input={input} caseId={id} user={user} />
                      ))
                    )}
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Edit Case Wizard Modal (Full Case & Quick Discussion) */}
        {editWizardOpen && (
          <ShareCaseWizard
            isOpen={editWizardOpen}
            onClose={() => setEditWizardOpen(false)}
            editMode={true}
            caseId={id}
            initialData={caseData}
          />
        )}

        {/* Delete Case Confirmation Modal */}
        {confirmDeleteCaseOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {isQuickDiscussion ? 'Delete Quick Discussion' : 'Delete Case'}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Are you sure you want to delete this {isQuickDiscussion ? 'discussion post' : 'clinical case'}? This action cannot be undone and will permanently remove all associated media and clinical inputs.
                </p>
                {deleteCaseError && (
                  <p className="text-xs text-red-600 mt-2 font-medium bg-red-50 p-2 rounded border border-red-100">{deleteCaseError}</p>
                )}
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setConfirmDeleteCaseOpen(false); setDeleteCaseError(''); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-lg transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCaseConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AppShell>
    );
};

export default CaseDetailPage;
